import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react'
import { reaction, autorun, toJS } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { Tabs, Table, Tooltip } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { LoadableButton, LoadableControl } from 'src/components'
import { $consts } from 'src/plugins'
import { getValidBtns, getValidNavColumns, controlFieldVerify } from '../helper'
import { getTableOperateColumns, renderTableCell } from '../tabler'
import { getLastDotString } from 'src/utils/tools'
import { cloneDeep, get, pick, uniq } from 'lodash'

const EditPageExpandTableContext = React.createContext<any>(undefined)

const EditableTable = observer((props: {
  config: any,
  defaultDataSource: any[],
  keyPath: string,
  onChange?: (data: any) => void
}) => {
  const editPageExpandTable = useContext(EditPageExpandTableContext)

  const state = useLocalObservable(() => {
    return {
      // 原始数据源
      dataSource: props.defaultDataSource,
      setDataSource (data: any[]) {
        this.dataSource = data
      },
      // 是否存在校验失败项
      checkVerifyIsInvalid () {
        const invalid: boolean[] = []
        const { Nav_Columns = [] } = props.config
        const copyDataSource = toJS(this.dataSource)
        copyDataSource.forEach((record: any) => {
          // 已删除 record 不校验
          if (record.IS_DELETED) return

          getValidNavColumns(Nav_Columns).forEach((col: any) => {
            const { FIELD_NAME } = col
            const verify = controlFieldVerify(col, get(record, FIELD_NAME))
            invalid.push(verify.invalid)
            // 往 __RECORD__ 里面添加 __VERIFY__ 校验结果
            Object.assign(record, {
              __VERIFY__: {
                ...record.__VERIFY__,
                [FIELD_NAME]: verify
              }
            })
          })
        })
        this.setDataSource(copyDataSource)
        return invalid.filter(item => item).length !== 0
      },
      // Table 实际显示的 dataSource
      get visibleDataSource () {
        // IS_DELETED 标记子表行数据是否被删除
        const targetSource = this.dataSource?.filter((data: any) => !data.IS_DELETED)
        // 排序
        const { Nav_PageEdit = {} } = props.config
        const { SORT_FIELDS = '', SORT_ORDERS = '' } = Nav_PageEdit
        const sortFields = SORT_FIELDS.split(',').filter((field: string) => !!field)
        const sortOrders = SORT_ORDERS.split(',')
        sortFields.forEach((field: string, index: number) => {
          const ascend = (Number(sortOrders[index]) || 0) === 0
          targetSource.sort((a: any, b: any) => {
            return a[field] > b[field] ? (ascend ? 1 : -1) : (ascend ? -1 : 1)
          })
        })
        return targetSource
      },
      // 当前选中项
      selectedKeys: [] as any,
      setSelectedKeys (keys: any[]) {
        this.selectedKeys = keys
      },
      // 单击选中的行数据
      selectedRow: {},
      setSelectedRow (record: any) {
        this.selectedRow = record
      },
      // 选中项配置
      get rowSelection () {
        return {
          fixed: 'left',
          type: 'checkbox',
          columnWidth: 32,
          selectedRowKeys: this.selectedKeys,
          // 单/多选
          onSelect: (...args: any[]) => {
            const [record, selected] = args
            let newKeys = [record.ID]
            if (selected) {
              newKeys = uniq(newKeys.concat(this.selectedKeys))
            } else {
              newKeys = this.selectedKeys.filter((key: string) => key !== newKeys[0])
            }
            this.setSelectedKeys(newKeys)
          },
          // 全选
          onSelectAll: (...args: any[]) => {
            let [selected, selectedRows, changeRows] = args
            // 这两货竟然会有 undefined 的 item 存在
            selectedRows = selectedRows.filter((row: any) => !!row)
            changeRows = changeRows.filter((row: any) => !!row)
            /**
             * 全选时，selectedRows 肯定是全选的数据
             * 取消全选时，changeRows 肯定是全选的数据
             */
            let newKeys = selected ? selectedRows.map((row: any) => row.ID) : changeRows.map((row: any) => row.ID)
            if (selected) {
              newKeys = uniq(newKeys.concat(this.selectedKeys))
            } else {
              newKeys = this.selectedKeys.filter((key: string) => newKeys.indexOf(key) === -1)
            }
            this.setSelectedKeys(newKeys)
          }
        }
      },
    }
  })

  // 暴露实例
  useEffect(() => {
    editPageExpandTable?.setTables({
      [props.config.Nav_PageEdit.ID]: state
    })
  }, [])

  useEffect(() => autorun(() => {
    const data = pick(state, ['dataSource', 'selectedKeys', 'selectedRow'])
    editPageExpandTable?.setTableData({ [props.config.Nav_PageEdit.ID]: data })
  }), [])

  // 单击选中的行数据
  const handleRenderRow = useCallback((record: any) => {
    return {
      onClick: () => {
        const row = state.dataSource.find((item: any) => item.ID === record.ID)
        state.setSelectedRow(row)
      }
    }
  }, [])

  // table 展开
  const [expandable, setExpandable] = useState<any>()
  useEffect(() => {
    setExpandable(props.config.HasChildren ? {
      columnWidth: 32,
      rowExpandable: () => true,
      expandedRowRender: (record: any, index: number) => {
        return (
          <EditPageTableExpand
            config={props.config}
            record={record}
            keyPath={`${props.keyPath}.${index}`}
            onChange={props.onChange}
          />
        )
      }
    } : undefined)
  }, [props.config.HasChildren])

  const columns = useMemo(() => {
    const { Nav_PageEdit = {}, Nav_Columns = [], Nav_Enums: enums = [] } = props.config
    // 排序
    const { SORT_FIELDS = '', SORT_ORDERS = '' } = Nav_PageEdit
    const sortFields = SORT_FIELDS.split(',').filter((field: string) => !!field)
    const sortOrders = SORT_ORDERS.split(',')
    // 列配置
    const commonColumns = getValidNavColumns(Nav_Columns).map((col: any) => {
      const { REQUIRED, LABEL, FIELD_NAME, VERIFY_ERROR_INFO, REMARK } = col
      const question = VERIFY_ERROR_INFO || REMARK
      const getTitle = () => {
        if (question) return <span><span className='pd-r4'>{LABEL}</span><Tooltip title={question}><QuestionCircleOutlined /></Tooltip></span>
        return LABEL
      }
      const sortIndex = sortFields.indexOf(FIELD_NAME)
      const sortOrder = Number(sortOrders[sortIndex]) || 0
      return {
        title: <span className={`edit-page__table-th ${REQUIRED ? 'required' : ''}`}>{getTitle()}</span>,
        key: FIELD_NAME,
        dataIndex: FIELD_NAME,
        sorter: {
          compare: (a: any, b: any) => {
            const ascend = sortOrder === 0
            return a[FIELD_NAME] > b[FIELD_NAME] ? (ascend ? 1 : -1) : (ascend ? -1 : 1)
          },
          multiple: sortIndex + 1,
        },
        showSorterTooltip: false,
        ellipsis: true,
        render: (text: any, record: any) => {
          if (record.__EDITABLE__) {
            return (
              <LoadableControl
                pageType={$consts['TABLE/FORM_TYPE_EDIT_PAGE']}
                locateControl={$consts['TABLE/CONTROL_LOCATE_ROW']}
                config={{ __CONTEXT__: editPageExpandTable, __RECORD__: record, ...col }}
              />
            )
          }
          return <span>{renderTableCell({ record, config: col, enums })}</span>
        }
      }
    })
    // 配置的操作按钮
    const operateColumns = getTableOperateColumns(
      editPageExpandTable?.activeTableRowBtns,
      {
        pageType: $consts['TABLE/FORM_TYPE_EDIT_PAGE'],
        locateBtn: $consts['TABLE/BTN_LOCATE_ROW'],
        config: { __CONTEXT__: editPageExpandTable }
      }
    )

    return commonColumns.concat(operateColumns)
  }, [editPageExpandTable?.activeTableRowBtns])

  return (
    <div className='edit-page__table-expand'>
      <Table
        rowKey='ID'
        size='small'
        columns={columns}
        dataSource={state.visibleDataSource}
        scroll={{ x: true }}
        pagination={false}
        rowSelection={state.rowSelection as any}
        onRow={handleRenderRow}
        expandable={expandable}
      />
    </div>
  )
})

const EditPageTableExpand = (props: {
  config: any,
  record: any,
  keyPath: string,
  onChange?: (data: any) => void
}) => {
  const state = useLocalObservable(() => {
    return {
      // 子表显示条件有效显示的子表
      get validTables () {
        const { Nav_PageEdit = {}, Nav_SubPageEdits = [] } = props.config
        const { Nav_PageEdit: deepConfig } = Nav_PageEdit

        const parentTables = Nav_SubPageEdits.filter((item: any) => !item.Nav_ParentPageDetailModel)
        if (!deepConfig?.SUB_TABLE_CONDITION) return parentTables

        const conditions = deepConfig.SUB_TABLE_CONDITION.split('|').map((item: string) => {
          const condArr = item.split(',')
          return {
            tableCode: condArr[0],
            fieldName: condArr[1],
            operate: condArr[2],
            value: condArr[3]
          }
        })
        return parentTables.filter((item: any) => {
          const relatedConds = conditions.filter((cond: any) => cond.tableCode === item.Nav_PageEdit?.CODE)
          return relatedConds.find((cond: any) => {
            const { fieldName, operate, value } = cond
            const fieldValue = String(get(props.record, fieldName))
            switch (Number(operate)) {
              case 1:
                return fieldValue === value
              default:
                return false
            }
          })
        })
      },
      // 所有有效子表实例
      tables: {} as any,
      setTables (table: any) {
        Object.assign(this.tables, table)
      },
      // 是否存在校验失败项
      checkVerifyIsInvalid () {
        const invalid: boolean[] = []
        Object.keys(this.tables).forEach(key => {
          const instance = this.tables[key]
          invalid.push(instance.checkVerifyIsInvalid())
        })
        return invalid.filter(item => item).length
      },
      // 所有有效子表数据
      tableData: {},
      setTableData (data: any) {
        Object.assign(this.tableData, data)
      },
      // 当前激活子表面板
      activeTableKey: '',
      setActiveTableKey (key: string) {
        this.activeTableKey = key
      },
      // 当前激活子表实例
      get activeTable () {
        return get(this.tables, this.activeTableKey)
      },
      // 当前激活子表配置
      get activeTableConfig () {
        const find = this.validTables.find((item: any) => item.Nav_PageEdit.ID === this.activeTableKey)
        return find || this.validTables[0] || {}
      },
      // 当前激活子表数据
      get activeTableData () {
        return get(this.tableData, this.activeTableKey)
      },
      // 当前激活子表配置按钮
      get activeTableEnableBtns () {
        return getValidBtns(this.activeTableConfig?.Nav_Btns)
      },
      // 当前激活子表列表头部按钮
      get activeTableHeaderBtns () {
        return this.activeTableEnableBtns.filter(btn => btn.BTN_FUN_TYPE === $consts['TABLE/BTN_FUN_TYPE_HEADER'])
      },
      // 当前激活子表列表配置的行按钮
      get activeTableRowBtns () {
        return this.activeTableEnableBtns.filter(btn => btn.BTN_FUN_TYPE === $consts['TABLE/BTN_FUN_TYPE_ROW'])
      }
    }
  })

  useEffect(() => reaction(
    () => JSON.stringify(state.tableData),
    () => {
      const targetData = {}
      for (const [key, value] of Object.entries(state.tableData) as any) {
        const find = state.validTables.find((item: any) => item.Nav_PageEdit.ID === key)
        find && Object.assign(targetData, {
          [`${props.keyPath}.${getLastDotString(find.NavFields)}`]: value.dataSource
        })
      }
      props.onChange?.(targetData)
    },
    { fireImmediately: true }
  ), [])

  useEffect(() => reaction(
    () => state.validTables[0]?.Nav_PageEdit?.ID,
    key => state.setActiveTableKey(key ?? ''),
    { fireImmediately: true }
  ), [])

  if (!state.validTables.length) return null

  return (
    <EditPageExpandTableContext.Provider value={state}>
      <Tabs
        items={
          state.validTables.map((config: any) => {
            const targetField = getLastDotString(config.NavFields)
            const dataSource = cloneDeep(get(props.record, targetField))
            return {
              key: config.Nav_PageEdit.ID,
              label: config.Nav_PageEdit.LABEL_NAME || '默认',
              children: (
                <EditableTable
                  config={config}
                  defaultDataSource={dataSource}
                  keyPath={`${props.keyPath}.${targetField}`}
                  onChange={props.onChange}
                />
              )
            }
          })
        }
        onChange={(key: string) => state.setActiveTableKey(key)}
        tabBarExtraContent={
          <div className='display-flex flex-rowfsc'>
            {
              state.activeTableHeaderBtns.map((btn: any, index: number, arr: any) => {
                const style = index === arr.length - 1 ? { marginRight: 0 } : { marginRight: 16 }
                return (
                  <div key={`${btn.ID}_${index}`} style={style}>
                    <LoadableButton
                      pageType={$consts['TABLE/FORM_TYPE_EDIT_PAGE']}
                      locateBtn={$consts['TABLE/BTN_LOCATE_HEADER']}
                      config={{ ...btn, __CONTEXT__: state, __PARENT_RECORD__: props.record }}
                    />
                  </div>
                )
              })
            }
          </div>
        }
        tabBarStyle={{ padding: '0 8px' }}
      />
    </EditPageExpandTableContext.Provider>
  )
}

export default observer(EditPageTableExpand)